import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  TaDashboardCountRowResponse,
  useLaborLevelDesignations,
} from '@sbv-clients/data-access-api';
import {
  FacilitySelectField,
  LaborLevelLevelsFacilitySelectField,
} from '@sbv-clients/domain-ui-shared';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import {
  Alert,
  Box,
  Card,
  Chip,
  Divider,
  Icon,
  Stack,
  Typography,
  useTheme,
} from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import { useTaDashboard } from '../../services/dashboard';
import { DashboardSkeleton } from './components/dashboard-skeleton';
import { PrimaryAvatarGroup } from './components/primary-avatar-group';
import { useCardSx } from './components/use-card-sx';
import { ViewAllButton } from './components/view-all-button';
import { PageParamsProvider, usePageParamsContext } from './params-context';
import { RecentlyViewedCard } from './recently-viewed-card';
import { calcPct, getGreetingAndLabel, sumMinutesToHours } from './transform';

// colorKey maps to theme.custom keys — all avatar colors live in theme.custom

const PunchedInCard = ({ count }: { count: number }) => {
  const navigate = useNavigate();
  const cardSx = useCardSx();
  return (
    <Card sx={cardSx} elevation={0}>
      <Typography variant="overline" color="grey.500" fontWeight={700}>
        Punched In
      </Typography>
      <Typography
        variant="h3"
        fontWeight="bold"
        color="text.primary"
        fontSize={36}
        py={3}
      >
        {count}
      </Typography>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={2.5}
      >
        <PrimaryAvatarGroup count={count} />
        <ViewAllButton
          onClick={() =>
            navigate('/employees?isPunchedIn=true', {
              state: { skipPersistedFilters: true },
            })
          }
        />
      </Box>
    </Card>
  );
};

const PendingRequestsCard = ({
  missingPunchCount,
  timeChangeCount,
}: {
  missingPunchCount: number;
  timeChangeCount: number;
}) => {
  const navigate = useNavigate();
  const cardSx = useCardSx();
  const pendingRequests = [
    {
      label: 'Missing Punch',
      count: missingPunchCount,
      to: '/employees?hasPendingMissingPunchRequest=true',
    },
    {
      label: 'Time Change',
      count: timeChangeCount,
      to: '/employees?hasPendingTimeChangeRequest=true',
    },
  ];
  const total = missingPunchCount + timeChangeCount;
  return (
    <Card sx={{ ...cardSx, pb: 0 }} elevation={0}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="overline" color="grey.500" fontWeight={700}>
          Pending Requests
        </Typography>
        <Chip
          label={`${total} total`}
          size="small"
          sx={{
            bgcolor: 'primary.light',
            color: 'primary.main',
            fontWeight: 600,
            borderRadius: '100px',
            border: '1px solid',
            borderColor: 'primary.main',
          }}
        />
      </Box>
      <Stack mt={2} divider={<Divider sx={{ mx: -5 }} />}>
        {pendingRequests.map((row) => (
          <Box
            key={row.label}
            display="flex"
            alignItems="center"
            gap={1.5}
            onClick={() =>
              navigate(row.to, { state: { skipPersistedFilters: true } })
            }
            sx={{ '&:hover': { bgcolor: 'grey.50' }, cursor: 'pointer' }}
            py={4}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" flex={1} fontWeight={500}>
              {row.label}
            </Typography>
            <Typography variant="body2" color="primary.main" fontWeight={600}>
              {row.count}
            </Typography>
            <Icon
              name="ChevronRight"
              customSizes={{ height: 20, width: 20 }}
              sx={{ color: 'text.secondary' }}
            />
          </Box>
        ))}
      </Stack>
    </Card>
  );
};

const StatCard = ({
  label,
  value,
  prev,
  pct,
  up,
}: {
  label: string;
  value: number;
  prev: number;
  pct: number;
  up: boolean;
}) => {
  const cardSx = useCardSx();
  return (
    <Card
      sx={{ ...cardSx, display: 'flex', flexDirection: 'column', pb: 3 }}
      elevation={0}
    >
      <Box flex={1}>
        <Typography variant="overline" color="grey.500" fontWeight={700}>
          {label}
        </Typography>
        <Box display="flex" alignItems="center" gap={3} my={3}>
          <Typography
            variant="h3"
            fontWeight="bold"
            color="text.primary"
            fontSize={36}
          >
            {value}
          </Typography>
          <Chip
            size="small"
            icon={
              up ? (
                <Icon
                  name="KeyboardArrowUp"
                  customSizes={{ height: 20, width: 20 }}
                />
              ) : (
                <Icon
                  name="KeyboardArrowDown"
                  customSizes={{ height: 20, width: 20 }}
                />
              )
            }
            label={`${pct}%`}
            sx={{
              fontWeight: 600,
              borderRadius: '100px',
              bgcolor: up ? 'success.light' : 'error.light',
              color: up ? 'success.dark' : 'error.main',
              '& .MuiChip-icon': {
                color: up ? 'success.dark' : 'error.main',
              },
            }}
          />
        </Box>
        <Typography variant="body2" color="grey.500" mt={2} fontSize={12}>
          vs{' '}
          <Box component="span" color="grey.300" fontWeight={600} fontSize={18}>
            {prev}
          </Box>{' '}
          last period
        </Typography>
      </Box>
    </Card>
  );
};

const ApproachingOTCard = ({
  approachingOt,
}: {
  approachingOt: TaDashboardCountRowResponse[];
}) => {
  const { isMultiFacility } = useAuth();
  const navigate = useNavigate();
  const cardSx = useCardSx();
  const { values, setValues } = usePageParamsContext();

  const { data: laborLevelDesignations } = useLaborLevelDesignations();

  const count = useMemo(() => {
    const facilityMatch = (r: TaDashboardCountRowResponse) =>
      !isMultiFacility ||
      values.facilityId == null ||
      r.facilityId === values.facilityId;
    const departmentMatch = (r: TaDashboardCountRowResponse) =>
      values.departmentId == null ||
      r.departmentLaborLevelId === values.departmentId;

    return approachingOt
      .filter((r) => facilityMatch(r) && departmentMatch(r))
      .reduce((acc, r) => acc + (r.employeeCount ?? 0), 0);
  }, [approachingOt, isMultiFacility, values.facilityId, values.departmentId]);
  return (
    <Card sx={{ ...cardSx, pb: 3 }} elevation={0}>
      <Typography variant="overline" color="grey.500" fontWeight={700}>
        Approaching OT
      </Typography>
      <Typography
        variant="h3"
        fontWeight="bold"
        color="text.primary"
        fontSize={36}
        my={3}
      >
        {count}
      </Typography>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={2.5}
      >
        <PrimaryAvatarGroup count={count} />
        <ViewAllButton
          onClick={() => {
            const params = new URLSearchParams({ isApproachingOt: 'true' });
            if (
              values.departmentId != null &&
              laborLevelDesignations?.department?.level != null
            )
              params.set(
                `laborLevel${laborLevelDesignations.department.level}`,
                String(values.departmentId)
              );
            if (
              isMultiFacility &&
              values.facilityId != null &&
              laborLevelDesignations?.facility?.level != null
            )
              params.set(
                `laborLevel${laborLevelDesignations.facility.level}`,
                String(values.facilityId)
              );
            navigate(`/employees?${params}`, {
              state: { skipPersistedFilters: true },
            });
          }}
        />
      </Box>
      <Divider sx={{ my: 3, mx: -5 }} />
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: '1fr 1fr',
          alignItems: 'flex-end',
        }}
      >
        {isMultiFacility && (
          <FacilitySelectField
            fullWidth
            density="compact"
            value={values.facilityId ?? ''}
            onChange={(e) =>
              setValues({
                facilityId: e.target.value as number,
                departmentId: null,
              })
            }
          />
        )}
        {!!laborLevelDesignations && (
          <LaborLevelLevelsFacilitySelectField
            fullWidth
            density="compact"
            value={values.departmentId ?? ''}
            level={laborLevelDesignations?.department?.level as number}
            onChange={(e) =>
              setValues({ departmentId: e.target.value as number })
            }
          />
        )}
      </Box>
    </Card>
  );
};

const FacilityDashboardContent = () => {
  const theme = useTheme();
  const { username } = useAuth();
  const { data, isLoading, isError, error } = useTaDashboard();
  const { greeting, todayLabel } = useMemo(() => getGreetingAndLabel(), []);

  const punchedInCount = data?.punchedInCount ?? 0;
  const missingPunchCount = data?.pendingMissingPunchEmployeeCount ?? 0;
  const timeChangeCount = data?.pendingTimeChangeEmployeeCount ?? 0;

  const hours = data?.hours ?? [];
  const totalHoursCurr = sumMinutesToHours(hours, 'totalMinutesCurrent');
  const totalHoursPrev = sumMinutesToHours(hours, 'totalMinutesPrevious');
  const otHoursCurr = sumMinutesToHours(hours, 'otMinutesCurrent');
  const otHoursPrev = sumMinutesToHours(hours, 'otMinutesPrevious');

  return (
    <Box
      sx={{
        bgcolor: theme.background.lightGrey,
        p: 8,
        minHeight: '100%',
        overflowY: 'auto',
        height: '100%',
      }}
    >
      <Box>
        <Typography variant="body2" color="grey.500" fontSize={12}>
          {todayLabel}
        </Typography>
        <Typography variant="h5" fontWeight={700} fontSize={24} mt={2}>
          {greeting}, {username}
        </Typography>
      </Box>
      <Divider sx={{ my: 8, mx: -8 }} />
      {isError ? (
        <Alert severity="error">{getApiErrorMessage(error)}</Alert>
      ) : isLoading ? (
        <DashboardSkeleton />
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' },
              gap: 4,
              mb: 4,
            }}
          >
            <PunchedInCard count={punchedInCount} />
            <PendingRequestsCard
              missingPunchCount={missingPunchCount}
              timeChangeCount={timeChangeCount}
            />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 4,
              mb: 4,
            }}
          >
            <StatCard
              label="Total Hours"
              value={totalHoursCurr}
              prev={totalHoursPrev}
              pct={calcPct(totalHoursCurr, totalHoursPrev)}
              up={totalHoursCurr >= totalHoursPrev}
            />
            <StatCard
              label="Overtime Hours"
              value={otHoursCurr}
              prev={otHoursPrev}
              pct={calcPct(otHoursCurr, otHoursPrev)}
              up={otHoursCurr >= otHoursPrev}
            />
            <ApproachingOTCard approachingOt={data?.approachingOt ?? []} />
          </Box>
          <RecentlyViewedCard />
        </>
      )}
    </Box>
  );
};

export const FacilityDashboardPage = () => (
  <PageParamsProvider>
    <FacilityDashboardContent />
  </PageParamsProvider>
);
